import { CASH_COMMON_CURRENCIES, type CtmStatus, minorToDecimal, parseAmount } from '@felix/domain';
import type { StatusTone } from '../../StatusBadge.js';
import { formatMoney } from '../payroll/view.js';
import type { CashData, CashSection, CashTxn, CtmItem } from './types.js';

/**
 * Kasa ekranının saf yardımcıları (ADR 0093 §8): durum rozetleri, bölümün listesi, tutar girişi,
 * ay seçenekleri ve CSV dökümleri.
 */

export { formatMoney };

/** Özet kartı tutarı: kuruşsuzsa tam sayı ("10,000"), değilse iki ondalık. */
export function formatWhole(minor: number, language: string): string {
  if (minor % 100 !== 0) return formatMoney(minor, language);
  return new Intl.NumberFormat(language.startsWith('tr') ? 'tr-TR' : 'en-US', {
    maximumFractionDigits: 0,
  }).format(minor / 100);
}

/** CTM durumunun rozeti (ekran 3 "Status"). */
export function ctmStatusTone(status: CtmStatus): StatusTone {
  switch (status) {
    case 'received':
      return 'success';
    case 'pending':
      return 'warning';
    case 'returned':
      return 'danger';
    case 'approved':
    case 'transferred':
      return 'info';
    default:
      return 'neutral';
  }
}

/** İşlem türünün rozeti (ekran 4 "Type"). */
export function txnTone(type: string): StatusTone {
  if (type === 'ctm_received' || type === 'receipt' || type === 'exchange_in') return 'success';
  if (type === 'expense' || type === 'exchange_out') return 'danger';
  if (type === 'reversal' || type === 'adjustment') return 'warning';
  return 'neutral';
}

/**
 * Bölümün CTM listesi: Requests bütün talepler; Approvals ofis kararı ya da ödeme bekleyenler;
 * Receipt Confirmation teyit bekleyenler; History hepsi.
 */
export function ctmQueue(items: readonly CtmItem[], section: CashSection): CtmItem[] {
  switch (section) {
    case 'ctm-approvals':
      return items.filter((i) => i.status === 'pending' || i.status === 'approved');
    case 'ctm-receipts':
      return items.filter((i) => i.status === 'approved' || i.status === 'transferred');
    default:
      return [...items];
  }
}

/** Dile göre ondalık ayırıcı. */
export const decimalOf = (language: string): '.' | ',' => (language.startsWith('tr') ? ',' : '.');

/** Girişteki metinden kuruş; geçersizse `null`. */
export function amountOf(text: string, language: string): number | null {
  return parseAmount(text, decimalOf(language));
}

/** Kuruştan giriş metni (gruplamasız): "10000.50" / "10000,50". */
export function amountText(minor: number | null | undefined, language: string): string {
  if (minor === null || minor === undefined) return '';
  const text = minorToDecimal(minor);
  return decimalOf(language) === ',' ? text.replace('.', ',') : text;
}

/** Ay seçicisi: bu ay ve önceki 11 ay, yeniden eskiye. */
export function cashMonthOptions(today: string, count = 12): string[] {
  const [year, month] = today.split('-').map(Number);
  const out: string[] = [];
  for (let i = 0; i < count; i += 1) {
    const date = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1 - i, 1));
    out.push(date.toISOString().slice(0, 7));
  }
  return out;
}

/** Gemi saatinde gün ve saat ("20.10.2026 14:10" biçimi arayüzün işidir). */
export function localTimeOf(txn: Pick<CashTxn, 'occurredAt' | 'tzOffsetMin'>): string {
  return new Date(Date.parse(txn.occurredAt) + txn.tzOffsetMin * 60_000)
    .toISOString()
    .slice(11, 16);
}

function csvCell(value: string | number | null): string {
  const text = value === null ? '' : String(value);
  return /[",\n;]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

/** Kasa defterinin CSV dökümü (Finance Reports): defter sırasıyla, ondalık noktalı. */
export function ledgerCsv(rows: readonly CashTxn[]): string {
  const head = [
    'Transaction ID',
    'Date',
    'Time',
    'Type',
    'Category',
    'Payee / Received From',
    'Port',
    'Receipt No',
    'Currency',
    'In',
    'Out',
    'Balance',
    'Linked',
    'Entered By',
    'Approved By',
    'Remarks',
  ];
  const lines = [...rows]
    .sort((a, b) => a.day.localeCompare(b.day) || a.occurredAt.localeCompare(b.occurredAt))
    .map((r) =>
      [
        r.txnNo,
        r.day,
        localTimeOf(r),
        r.txnType,
        r.category,
        r.payee,
        r.port,
        r.receiptNo,
        r.currency,
        r.direction === 'in' ? minorToDecimal(r.amount) : '',
        r.direction === 'out' ? minorToDecimal(r.amount) : '',
        minorToDecimal(r.balance),
        r.linkedRef,
        r.enteredBy,
        r.approvedBy,
        r.remarks,
      ]
        .map(csvCell)
        .join(','),
    );
  return [head.join(','), ...lines].join('\n');
}

/** CTM kaydının CSV dökümü (Finance Reports). */
export function ctmCsv(items: readonly CtmItem[]): string {
  const head = [
    'CTM Reference No',
    'Request Date',
    'Required Date',
    'Port / Country',
    'Currency',
    'Requested Amount',
    'Purpose',
    'Delivery Method',
    'Status',
    'Transferred',
    'Received',
    'Requested By',
  ];
  const lines = items.map((i) =>
    [
      i.ctmNo,
      i.requestDate,
      i.requiredDate,
      i.port,
      i.currency,
      minorToDecimal(i.requestedAmount),
      i.purpose,
      i.deliveryMethod,
      i.status,
      i.transfer ? minorToDecimal(i.transfer.amount) : '',
      i.receipt ? minorToDecimal(i.receipt.amount) : '',
      i.requestedBy.name,
    ]
      .map(csvCell)
      .join(','),
  );
  return [head.join(','), ...lines].join('\n');
}

/**
 * Seçicideki para birimleri (ADR 0110 §1). Ofis kasayı kurduysa yeni nakit (`in`: açılış, giriş,
 * bozdurmanın girişi, CTM talebi) yalnız açık kasaya; çıkış (`out`) açık kasalardan ve bakiyesi
 * olan birimlerden. Kasa kurulmamışsa eski davranış: yaygın birimler ve kasadaki birimler.
 */
export function cashCurrencyChoices(
  data: Pick<CashData, 'configured' | 'accounts' | 'currencies' | 'balances'>,
  direction: 'in' | 'out',
): string[] {
  if (!data.configured) return [...new Set([...CASH_COMMON_CURRENCIES, ...data.currencies])];
  const open = (data.accounts ?? []).filter((a) => a.open).map((a) => a.currency);
  const funded = direction === 'out' ? data.balances.filter((b) => b.balance > 0) : [];
  return [...new Set([...open, ...funded.map((b) => b.currency)])].sort();
}
